import { Schema } from "effect";

/**
 * The single coarse reason a patient-info operation was refused.
 *
 * Deliberately coarse. Every authority failure — wrong practice, wrong terminal, wrong workflow,
 * wrong patient, expired, revoked, already consumed, malformed, or simply unknown — collapses to
 * `unavailable`, so a caller cannot probe which binding was wrong or learn whether a patient record
 * exists. `retryable` is reserved for transient infrastructure trouble that a later attempt in the
 * same active capability may survive.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoUnavailableReason } from "@ascleo/frontdesk-contracts/patient-info/errors";
 *
 * Schema.decodeUnknownSync(PatientInfoUnavailableReason)("unavailable");
 * ```
 */
export const PatientInfoUnavailableReason = Schema.Literals(["unavailable", "retryable"]);

/**
 * The decoded type of a {@link PatientInfoUnavailableReason}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoUnavailableReason } from "@ascleo/frontdesk-contracts/patient-info/errors";
 *
 * const reason: PatientInfoUnavailableReason = "unavailable";
 * ```
 */
export type PatientInfoUnavailableReason = typeof PatientInfoUnavailableReason.Type;

/**
 * The one rejection every patient-info RPC returns when it will not serve a request.
 *
 * Carries no patient data, no capability value, no binding detail, and no vendor error. The client's
 * only correct response is to skip the optional review and continue to `done` — the completed
 * check-in or patient creation is never affected.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { PatientInfoUnavailableSchema } from "@ascleo/frontdesk-contracts/patient-info/errors";
 * import { Schema } from "effect";
 *
 * const error = Schema.decodeUnknownSync(PatientInfoUnavailableSchema)({
 *   _tag: "PatientInfoUnavailable",
 *   reason: "unavailable",
 * });
 * ```
 */
export const PatientInfoUnavailableSchema = Schema.Struct({
  _tag: Schema.Literal("PatientInfoUnavailable"),
  reason: PatientInfoUnavailableReason,
});

/**
 * The decoded type of a {@link PatientInfoUnavailableSchema}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoUnavailable } from "@ascleo/frontdesk-contracts/patient-info/errors";
 *
 * function isRetryable(error: PatientInfoUnavailable): boolean {
 *   return error.reason === "retryable";
 * }
 * ```
 */
export type PatientInfoUnavailable = typeof PatientInfoUnavailableSchema.Type;

/**
 * Rejection returned when a submitted proposal is not well formed.
 *
 * Distinct from {@link PatientInfoUnavailableSchema} because it is the patient's input that is at
 * fault, not their authority, and the client can correct it in place. It names only which review
 * items were at fault — never the offending value, which must not be echoed into logs or telemetry.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { PatientInfoProposalRejectedSchema } from "@ascleo/frontdesk-contracts/patient-info/errors";
 * import { Schema } from "effect";
 *
 * Schema.decodeUnknownSync(PatientInfoProposalRejectedSchema)({
 *   _tag: "PatientInfoProposalRejected",
 *   itemIds: ["itm_7f3a"],
 * });
 * ```
 */
export const PatientInfoProposalRejectedSchema = Schema.Struct({
  _tag: Schema.Literal("PatientInfoProposalRejected"),
  itemIds: Schema.Array(Schema.String),
});

/**
 * The decoded type of a {@link PatientInfoProposalRejectedSchema}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoProposalRejected } from "@ascleo/frontdesk-contracts/patient-info/errors";
 *
 * function count(error: PatientInfoProposalRejected): number {
 *   return error.itemIds.length;
 * }
 * ```
 */
export type PatientInfoProposalRejected = typeof PatientInfoProposalRejectedSchema.Type;
